import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { ChurchForm } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { FORM_STATUS_LABELS } from './labels';
import { QrCode } from '../nursery/QrCode';

/** Enlace para llenar el formulario (quien lo abre entra con su cuenta de la iglesia). */
export const formLink = (formId: string) => `${window.location.origin}/app/formularios/${formId}`;

/** Formularios (Motor 22): los abiertos para responder y, para quien administra, todos. */
export function FormsPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('forms.manage');
  const [params, setParams] = useSearchParams();
  const manage = canManage && params.get('vista') === 'administrar';
  const list = useAsync(
    (signal) =>
      churchId ? api.get<{ items: ChurchForm[] }>(`/forms/churches/${churchId}/forms?manage=${manage}`, signal).then((r) => r.items) : Promise.resolve([]),
    [churchId, manage],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Formularios</h1>
        <p className="muted">Inscripciones, encuestas y registros de tu iglesia.</p>
      </header>

      {canManage ? (
        <div className="tabs" role="tablist" aria-label="Formularios">
          <button type="button" role="tab" aria-selected={!manage} className={`tabs__tab${!manage ? ' tabs__tab--active' : ''}`} onClick={() => setParams({})}>
            Para responder
          </button>
          <button type="button" role="tab" aria-selected={manage} className={`tabs__tab${manage ? ' tabs__tab--active' : ''}`} onClick={() => setParams({ vista: 'administrar' })}>
            Administrar
          </button>
        </div>
      ) : null}

      {manage ? (
        <div className="actions">
          <Link className="btn btn--primary" to="/app/formularios/nuevo">
            <span>Nuevo formulario</span>
          </Link>
        </div>
      ) : null}

      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' && list.data.length === 0 ? (
        <EmptyState title={manage ? 'Aún no hay formularios.' : 'No hay formularios abiertos por ahora.'} />
      ) : null}
      {list.status === 'ready' && list.data.length > 0 ? (
        <Card>
          <ul className="list">
            {list.data.map((f) => (manage ? <ManageRow key={f.id} churchId={churchId} form={f} onChange={list.reload} /> : <OpenRow key={f.id} form={f} />))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

function OpenRow({ form: f }: { form: ChurchForm }) {
  return (
    <li className="list__item">
      <Link className="list__link" to={`/app/formularios/${f.id}`}>
        <strong>{f.title}</strong>
        {f.description ? <span className="muted small">{f.description}</span> : null}
        {f.answered ? <span className="badge badge--active">Ya respondiste</span> : null}
      </Link>
    </li>
  );
}

function ManageRow({ churchId, form: f, onChange }: { churchId: string; form: ChurchForm; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const link = formLink(f.id);

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setError(`No se pudo copiar. El enlace es: ${link}`);
    }
  }

  async function setStatus(action: 'open' | 'close') {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/forms/churches/${churchId}/forms/${f.id}/${action}`);
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="list__item list__item--actions">
      <div className="list__link">
        <strong>{f.title}</strong>
        <span className="muted small">
          <span className="badge">{FORM_STATUS_LABELS[f.status]}</span> {f.responseCount === 1 ? '1 respuesta' : `${f.responseCount} respuestas`}
        </span>
      </div>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        {f.status === 'draft' ? (
          <Link className="btn btn--ghost" to={`/app/formularios/${f.id}/editar`}>
            <span>Editar</span>
          </Link>
        ) : null}
        {f.status !== 'open' ? (
          <Button variant="secondary" busy={busy} onClick={() => setStatus('open')}>
            {f.status === 'draft' ? 'Publicar' : 'Reabrir'}
          </Button>
        ) : (
          <Button variant="secondary" busy={busy} onClick={() => setStatus('close')}>
            Cerrar
          </Button>
        )}
        {f.status !== 'draft' ? (
          <Link className="btn btn--ghost" to={`/app/formularios/${f.id}/respuestas`}>
            <span>Respuestas</span>
          </Link>
        ) : null}
        {f.status === 'open' ? (
          <>
            <Button variant="ghost" onClick={() => void copy()}>
              {copied ? 'Enlace copiado' : 'Copiar enlace'}
            </Button>
            <Button variant="ghost" aria-expanded={qr} onClick={() => setQr((x) => !x)}>
              {qr ? 'Ocultar QR' : 'Ver QR'}
            </Button>
          </>
        ) : null}
      </div>
      {qr ? (
        <div className="qr-print">
          <QrCode value={link} label={`Código QR del formulario «${f.title}»`} />
          <p className="center">
            <strong>{f.title}</strong>
            <br />
            Escanea para llenarlo.
          </p>
          <Button variant="secondary" onClick={() => window.print()}>
            Imprimir QR
          </Button>
        </div>
      ) : null}
    </li>
  );
}
